// src/components/auth/LoginForm.jsx
import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';

export default function LoginForm() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [light, setLight] = useState({ x: 50, y: 35 });

  async function onSubmit(e) {
    e.preventDefault();
    setMsg('');
    setLoading(true);
    try {
      const r = await login(username, password);
      if (r.ok) {
        // Redirección exitosa
        window.location.href = '/';
      } else {
        setMsg(r.message || 'Credenciales inválidas');
      }
    } catch (error) {
      console.error('Error en login:', error);
      setMsg('Error al intentar iniciar sesión');
    } finally {
      setLoading(false);
    }
  }

  function handleMouseMove(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setLight({ x, y });
  }

  return (
    <div
      className="relative flex min-h-[100dvh] items-center justify-center overflow-hidden bg-slate-950 px-4 sm:px-6"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setLight({ x: 50, y: 35 })}
    >
      <div className="pointer-events-none absolute inset-0">
        <div
          className="absolute inset-0 opacity-80 transition-all duration-300"
          style={{
            background: `radial-gradient(520px circle at ${light.x}% ${light.y}%, rgba(16, 185, 129, 0.35), rgba(15, 23, 42, 0) 55%)`
          }}
        />
        <div className="absolute -left-24 -top-24 h-80 w-80 rounded-full bg-emerald-500/25 blur-3xl" />
        <div className="absolute -right-24 top-1/3 h-96 w-96 rounded-full bg-cyan-500/20 blur-3xl" />
        <div className="absolute bottom-0 left-1/2 h-64 w-[32rem] -translate-x-1/2 rounded-full bg-blue-500/15 blur-3xl" />
      </div>

      <div className="relative mx-auto grid w-full max-w-5xl overflow-hidden rounded-3xl border border-white/15 bg-white/95 shadow-2xl backdrop-blur-xl lg:grid-cols-[1.1fr_0.9fr]">
        <section className="hidden bg-gradient-to-br from-emerald-700 via-emerald-600 to-cyan-700 p-10 text-white lg:block">
          <div className="flex h-full flex-col justify-between">
            <div>
              <img src="/logo-elements.png" alt="Elements" className="h-20 w-20 rounded-2xl bg-white/15 p-2.5 backdrop-blur" />
              <h1 className="mt-6 text-3xl font-black tracking-tight">Control total del sistema</h1>
              <p className="mt-3 max-w-sm text-sm text-white/90">
                Accede al panel operativo de Elements para gestionar servicios, usuarios y procesos en un solo lugar.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="rounded-xl border border-white/20 bg-white/10 p-3">
                <p className="font-semibold">Operacion continua</p>
                <p className="mt-1 text-white/80">Monitorea pendientes y tickets en tiempo real.</p>
              </div>
              <div className="rounded-xl border border-white/20 bg-white/10 p-3">
                <p className="font-semibold">Seguridad</p>
                <p className="mt-1 text-white/80">Autenticacion centralizada por roles.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="p-6 sm:p-10">
          <div className="mx-auto w-full max-w-md">
            <div className="mb-7 text-center lg:text-left">
              <img src="/logo-elements.png" alt="Elements" className="mx-auto h-20 w-20 rounded-2xl border border-slate-200 object-contain p-2 lg:mx-0" />
              <h2 className="mt-5 text-2xl font-extrabold tracking-tight text-slate-900">Bienvenido de nuevo</h2>
              <p className="mt-1.5 text-sm text-slate-600">Inicia sesion para continuar en Elements.</p>
            </div>

            {msg && (
              <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-3.5 py-3 text-sm font-medium text-red-700" role="alert" aria-live="polite">
                {msg}
              </div>
            )}

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label htmlFor="username" className="mb-1.5 block text-sm font-semibold text-slate-700">Usuario</label>
                <input
                  id="username"
                  autoComplete="username"
                  placeholder="Ingresa tu usuario"
                  className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 placeholder:text-slate-400 transition focus:border-emerald-500 focus:outline-none focus:ring-4 focus:ring-emerald-100"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  required
                />
              </div>

              <div>
                <label htmlFor="password" className="mb-1.5 block text-sm font-semibold text-slate-700">Contrasena</label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder="Ingresa tu contrasena"
                  className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 placeholder:text-slate-400 transition focus:border-emerald-500 focus:outline-none focus:ring-4 focus:ring-emerald-100"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="mt-2 inline-flex w-full items-center justify-center rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-700/25 transition hover:bg-emerald-700 focus:outline-none focus:ring-4 focus:ring-emerald-200 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? 'Ingresando...' : 'Ingresar al sistema'}
              </button>
            </form>

            <p className="mt-5 text-center text-xs text-slate-500 lg:text-left">
              Plataforma interna Elements. Uso exclusivo del personal autorizado.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
